import { useState } from "react";
import { useTranslation, untranslated } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import Text from "@CubeComponents/html/Text/Text";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { revokeExternalLinks, type ExternalLinkListItem } from "./externalLinks.api";

/**
 * Confirmation before revoking one link or a multi-selection (QA r8 #5).
 *
 * The warning spells out that the effect is immediate — a recipient with the page already open loses
 * access on their next action, not at the end of some session. One request carries every id; the
 * server checks each link against its own item, so a mixed selection revokes what it can and the
 * dialog reports how many it could not.
 */
type RevokeExternalLinkModalProps = {
  links: ExternalLinkListItem[];
  onClose: () => void;
  onRevoked: () => void;
};

function RevokeExternalLinkModal({ links, onClose, onRevoked }: Readonly<RevokeExternalLinkModalProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [busy, setBusy] = useState(false);
  const count = links.length;
  const single = count === 1 ? links[0] : undefined;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const res = await revokeExternalLinks(links.map((link) => link._id));
      if (!res.status) {
        dispatch(
          showToastNotification({
            content: untranslated(res.errorMessage || SOMETHING_WENT_WRONG),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }
      if (res.failed.length > 0) {
        dispatch(
          showToastNotification({
            content: t("{{count}} of {{total}} links could not be revoked", { count: res.failed.length, total: count }),
            type: TOAST_TYPE_WARNING,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: single ? t("Link revoked") : t("{{count}} links revoked", { count }),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      }
      onRevoked();
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="27.5rem"
      headerTitle={single ? t("Revoke link") : t("Revoke links")}
      onCancel={onClose}
      modelContent={
        <div className="flex flex-col gap-3 py-2">
          <Text className="text-sm">
            {single
              ? t("Are you sure you want to revoke this link?")
              : t("Are you sure you want to revoke these {{count}} links?", { count })}
          </Text>
          <div className="rounded-md border border-warning bg-bg_warning px-3 py-2">
            <Text noWrap className="text-xs leading-relaxed">
              {single
                ? t(
                    '"{{name}}" will stop working immediately and recipients will see an invalid-link page. Access history is retained for audit.',
                    { name: single.name },
                  )
                : t(
                    "{{count}} links will stop working immediately and recipients will see an invalid-link page. Access history is retained for audit.",
                    { count },
                  )}
            </Text>
          </div>
        </div>
      }
      footerContent={
        <div className="flex gap-3">
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} disabled={busy} onClick={handleConfirm}>
            {t("Confirm")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

export default RevokeExternalLinkModal;
